import { attentionGuardrailChecks } from "./requisitionGuardrailChainView";
import { realRuleName } from "./guardrailRuleLabels";
import type {
  GuardrailCheck,
  GuardrailResult,
} from "./RequisitionGuardrailOutcome";

const trim = (value: string | null | undefined) => value?.trim() ?? "";

/** BLOCK failures and UNKNOWN states that drive HARD_BLOCK. */
export const blockingGuardrailChecks = (
  checks: readonly GuardrailCheck[]
): GuardrailCheck[] =>
  attentionGuardrailChecks(checks).filter(
    (check) => (check.effect ?? "BLOCK") === "BLOCK"
  );

export const isGuardrailHardBlockResult = (
  result: GuardrailResult | null | undefined
): boolean => result?.guardrailDecision === "HARD_BLOCK";

const ruleLabel = (check: GuardrailCheck): string => {
  const name = realRuleName(check);
  return name === "" ? "Unnamed rule" : name;
};

const joinLabels = (labels: string[]): string => {
  if (labels.length <= 1) {
    return labels[0] ?? "";
  }
  const head = labels.slice(0, -1).join(", ");
  return `${head} and ${labels[labels.length - 1]}`;
};

const stripRulePrefix = (detail: string, label: string): string => {
  if (label === "") {
    return detail;
  }
  const heads = [`${label}: fail — `, `${label}: fail - `, `${label}: fail`];
  for (const head of heads) {
    if (detail.startsWith(head)) {
      return detail.slice(head.length).trim();
    }
  }
  return detail;
};

const checkEvidence = (check: GuardrailCheck): string => {
  const label = ruleLabel(check);
  const detail = stripRulePrefix(trim(check.detail), label);
  if (detail !== "") {
    return detail;
  }
  const description = trim(check.description);
  if (description !== "") {
    return description;
  }
  if (check.state === "UNKNOWN") {
    return "A required value is missing for this rule.";
  }
  return "This blocking rule did not pass.";
};

const hardBlockTitle = (blocking: readonly GuardrailCheck[]): string => {
  if (blocking.length === 0) {
    return "Hard-blocked";
  }
  const labels = blocking.map(ruleLabel);
  if (blocking.length === 1) {
    return `Hard-blocked by ${labels[0]}`;
  }
  return `Hard-blocked by ${joinLabels(labels)}`;
};

const hardBlockNextStep = (blocking: readonly GuardrailCheck[]): string => {
  const missingFact = blocking.some((check) =>
    trim(check.detail).toLowerCase().startsWith("missing fact:")
  );
  if (missingFact) {
    return "Fill in the missing field on the position or hiring request, then edit and save the requisition.";
  }
  return "The requisition stays in Draft. Change the details that failed the rule, or ask your workforce admin to adjust the rule.";
};

export type HardBlockNoticeCopy = {
  title: string;
  reasons: string[];
  nextStep: string;
};

/** User-facing copy from a stored guardrail evaluation. */
export const hardBlockNoticeCopy = (
  result: GuardrailResult | null | undefined
): HardBlockNoticeCopy | null => {
  if (!isGuardrailHardBlockResult(result)) {
    return null;
  }
  const checks = result?.guardrailEvaluation?.checks ?? [];
  const blocking = blockingGuardrailChecks(checks);
  const reasons =
    blocking.length > 0
      ? blocking.map((check) => {
          const label = ruleLabel(check);
          const evidence = checkEvidence(check);
          return label === evidence ? evidence : `${label}: ${evidence}`;
        })
      : ["A blocking workforce rule stopped this requisition."];
  return {
    title: hardBlockTitle(blocking),
    reasons,
    nextStep: hardBlockNextStep(blocking),
  };
};

/** One-line headline for the outcome card when the chain has no human steps. */
export const hardBlockOutcomeHeadline = (
  checks: readonly GuardrailCheck[]
): string => {
  const blocking = blockingGuardrailChecks(checks);
  const copy = hardBlockNoticeCopy({
    guardrailDecision: "HARD_BLOCK",
    guardrailEvaluation: { decision: "HARD_BLOCK", checks: [...checks] },
  });
  if (copy == null) {
    return "Hard-blocked";
  }
  const lone = blocking[0];
  if (blocking.length === 1 && lone) {
    return `${copy.title} — ${checkEvidence(lone)}`;
  }
  return copy.title;
};
